import React, { useState, useEffect } from 'react';
import { 
  Flag, AlertTriangle, CheckCircle, XCircle, Building2, User, 
  Loader2, Search, Filter, RefreshCw, ExternalLink, Clock, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminAgencyReviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({ pending: 0, under_review: 0, cleared: 0, suspended: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedListing, setSelectedListing] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [ownerSignals, setOwnerSignals] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/flagged-listings`, {
          params: { status: statusFilter || undefined },
          headers: getAdminHeaders()
        }),
        axios.get(`${API}/admin/flagged-listings/stats`, { headers: getAdminHeaders() })
      ]);
      setListings(listingsRes.data.listings || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch flagged listings:', error);
      toast.error('Failed to load flagged listings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = async (listing) => {
    setSelectedListing(listing);
    setReviewNotes('');
    setReviewDialogOpen(true);
    
    // Fetch owner signals
    try {
      const res = await axios.get(`${API}/admin/owners/${listing.owner_id}/signals`, {
        headers: getAdminHeaders()
      });
      setOwnerSignals(res.data);
    } catch (error) {
      console.error('Failed to fetch owner signals:', error);
    }
  };

  const handleReviewDecision = async (decision) => {
    if (!selectedListing) return;
    
    setReviewSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/listings/${selectedListing.property_id}/review`,
        { decision, notes: reviewNotes || null },
        { headers: getAdminHeaders() }
      );
      toast.success(`Listing ${decision === 'clear' ? 'cleared' : 'suspended'} successfully`);
      setReviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      under_review: 'bg-blue-100 text-blue-800',
      cleared: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const getReasonLabel = (reason) => {
    const labels = {
      property_manager: 'Property Manager',
      agency: 'Real Estate Agency',
      multiple_properties: 'Multiple Listings',
      business_account: 'Business Account',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  return (
    <div className="p-8" data-testid="admin-agency-review-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Flag className="w-7 h-7 text-amber-400" />
            Agency Review Queue
          </h1>
          <p className="text-slate-400 mt-1">
            Review flagged listings for potential agency violations
          </p>
        </div>
        <Button 
          variant="outline" 
          className="border-slate-600 text-slate-300 hover:text-white"
          onClick={fetchData}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div 
          className={`bg-slate-800 rounded-xl p-4 cursor-pointer border-2 transition-colors ${
            statusFilter === 'pending' ? 'border-amber-500' : 'border-transparent hover:border-slate-600'
          }`}
          onClick={() => setStatusFilter('pending')}
          data-testid="filter-pending"
        >
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <Clock className="w-5 h-5" />
            <span className="text-sm font-medium">Pending</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.pending}</p>
        </div>
        
        <div 
          className={`bg-slate-800 rounded-xl p-4 cursor-pointer border-2 transition-colors ${
            statusFilter === 'under_review' ? 'border-blue-500' : 'border-transparent hover:border-slate-600'
          }`}
          onClick={() => setStatusFilter('under_review')}
          data-testid="filter-under-review"
        >
          <div className="flex items-center gap-2 text-blue-400 mb-2">
            <Eye className="w-5 h-5" />
            <span className="text-sm font-medium">Under Review</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.under_review}</p>
        </div>
        
        <div 
          className={`bg-slate-800 rounded-xl p-4 cursor-pointer border-2 transition-colors ${
            statusFilter === 'cleared' ? 'border-green-500' : 'border-transparent hover:border-slate-600'
          }`}
          onClick={() => setStatusFilter('cleared')}
          data-testid="filter-cleared"
        >
          <div className="flex items-center gap-2 text-green-400 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Cleared</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.cleared}</p>
        </div>
        
        <div 
          className={`bg-slate-800 rounded-xl p-4 cursor-pointer border-2 transition-colors ${
            statusFilter === 'suspended' ? 'border-red-500' : 'border-transparent hover:border-slate-600'
          }`}
          onClick={() => setStatusFilter('suspended')}
          data-testid="filter-suspended"
        >
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <XCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Suspended</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.suspended}</p>
        </div>
        
        <div 
          className={`bg-slate-800 rounded-xl p-4 cursor-pointer border-2 transition-colors ${
            statusFilter === '' ? 'border-slate-500' : 'border-transparent hover:border-slate-600'
          }`}
          onClick={() => setStatusFilter('')}
          data-testid="filter-all"
        >
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <Filter className="w-5 h-5" />
            <span className="text-sm font-medium">All Flagged</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.total}</p>
        </div>
      </div>

      {/* Listings Table */}
      <div className="bg-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
          </div>
        ) : listings.length === 0 ? (
          <div className="p-12 text-center">
            <Flag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No flagged listings found</p>
            <p className="text-slate-500 text-sm mt-1">
              {statusFilter ? `Try changing the status filter` : 'All clear!'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-300">Listing</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-300">Owner</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-300">Reports</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-300">Status</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {listings.map((listing) => (
                <tr key={listing.property_id} className="hover:bg-slate-700/30" data-testid={`listing-row-${listing.property_id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                        {listing.photos?.[0] ? (
                          <img src={listing.photos[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium truncate max-w-[200px]">{listing.title}</p>
                        <p className="text-slate-400 text-sm">{listing.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-slate-600 text-white text-sm">
                          {listing.owner_name?.charAt(0) || 'O'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-white text-sm">{listing.owner_name || 'Unknown'}</p>
                        <p className="text-slate-400 text-xs">
                          {listing.owner_listing_count} listing{listing.owner_listing_count !== 1 ? 's' : ''}
                          {!listing.owner_attestation_completed && (
                            <span className="text-amber-400 ml-2">• No attestation</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-amber-500 text-amber-400">
                        {listing.report_count || listing.flag_count || 0} report{(listing.report_count || listing.flag_count || 0) !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(listing.flag_status)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:text-white"
                      onClick={() => handleOpenReview(listing)}
                      data-testid={`review-btn-${listing.property_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-500" />
              Review Flagged Listing
            </DialogTitle>
            <DialogDescription>
              Review the reports and owner information to make a decision
            </DialogDescription>
          </DialogHeader>

          {selectedListing && (
            <div className="space-y-6 mt-4">
              {/* Listing Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-start gap-4">
                  <div className="w-24 h-20 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                    {selectedListing.photos?.[0] ? (
                      <img src={selectedListing.photos[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{selectedListing.title}</h3>
                    <p className="text-slate-600 text-sm">{selectedListing.address}, {selectedListing.city}</p>
                    <p className="text-slate-500 text-sm mt-1">${selectedListing.price_per_month}/month</p>
                    <a 
                      href={`/property/${selectedListing.property_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-trust-blue text-sm inline-flex items-center gap-1 mt-2 hover:underline"
                    >
                      View listing <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Owner Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Owner Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Name</p>
                    <p className="text-slate-900">{selectedListing.owner_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="text-slate-900">{selectedListing.owner_email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Listings</p>
                    <p className={`font-medium ${selectedListing.owner_listing_count > 5 ? 'text-red-600' : 'text-slate-900'}`}>
                      {selectedListing.owner_listing_count}
                      {selectedListing.owner_listing_count > 5 && ' ⚠️'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Attestation</p>
                    <p className={selectedListing.owner_attestation_completed ? 'text-green-600' : 'text-amber-600'}>
                      {selectedListing.owner_attestation_completed ? '✓ Completed' : '✗ Not completed'}
                    </p>
                  </div>
                </div>

                {/* Risk Score */}
                {ownerSignals && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Risk Score</span>
                      <div className={`flex items-center gap-2 ${
                        ownerSignals.risk_score > 50 ? 'text-red-600' : 
                        ownerSignals.risk_score > 25 ? 'text-amber-600' : 'text-green-600'
                      }`}>
                        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              ownerSignals.risk_score > 50 ? 'bg-red-500' : 
                              ownerSignals.risk_score > 25 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${ownerSignals.risk_score}%` }}
                          />
                        </div>
                        <span className="font-medium">{ownerSignals.risk_score}/100</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Reports */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Reports ({selectedListing.reports?.length || 0})
                </h4>
                {selectedListing.reports?.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {selectedListing.reports.map((report, idx) => (
                      <div key={report.report_id || idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-amber-100 text-amber-800">
                            {getReasonLabel(report.reason)}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {new Date(report.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {report.details && (
                          <p className="text-sm text-amber-700">{report.details}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No detailed reports available</p>
                )}
              </div>

              {/* Review Notes */}
              <div>
                <Label className="mb-2 block">Review Notes (optional)</Label>
                <Textarea
                  placeholder="Add notes about your decision..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  data-testid="review-notes"
                />
              </div>

              {/* Decision Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleReviewDecision('clear')}
                  disabled={reviewSubmitting}
                  data-testid="clear-listing-btn"
                >
                  {reviewSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Clear Listing
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => handleReviewDecision('suspend')}
                  disabled={reviewSubmitting}
                  data-testid="suspend-listing-btn"
                >
                  {reviewSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Suspend Listing
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAgencyReviewPage;
